<script setup lang="ts">
    import { onMounted, reactive, ref, watch } from 'vue';
    import axios from 'axios';
    import { useRoute, useRouter } from 'vue-router';
    import CustomUploadInput from '@/components/CustomUploadInput.vue';
    import { QuillEditor } from '@vueup/vue-quill'
    import '@vueup/vue-quill/dist/vue-quill.snow.css'

    const router = useRouter();
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const isFormValid = ref(false);
    const formData = reactive({
        id: "",
        libelle: "",
        contenu_aide: "",
        fichier_photo: null as any,
        fichier_photo_preview: '/assets/users/default.png',
        fichier_video: null as any,
        fichier_video_preview: '/assets/users/default.png',
        categorie_aide_id: ""
    });
    const errors = reactive({
        libelle: "",
        contenu_aide: "",
        fichier_photo: "",
        categorie_aide_id: "",
    });
    const startupFormDatas = reactive({
        categories: [] as any[]
    });

    onMounted(() => {
        const form_datas: any = route.meta.form_datas;
        startupFormDatas.categories = form_datas.categories;
        if (form_datas?.tutoriel !== undefined) {
            const item = form_datas.tutoriel as any;
            formData.id = item.id;
            formData.libelle = item.libelle;
            formData.contenu_aide = item.contenu_aide;
            if (item.fichier_photo !== undefined) {
                formData.fichier_photo_preview = item.fichier_photo_url;
            }
            if (item.fichier_video !== undefined) {
                formData.fichier_video_preview = item.fichier_video_url;
            }
            formData.categorie_aide_id = item.categorie_aide_id;
        }
    });

    watch(formData, (oldFormData, newFormData) => {
        validate();
    });

    function validate() {
        let isValid = true;

        errors.libelle = '';
        if (!formData.libelle) {
            errors.libelle ="Le libellé du tutoriel est requis";
            isValid = false;
        }

        errors.contenu_aide = '';
        if (!formData.contenu_aide) {
            errors.contenu_aide ="Le contenu du tutoriel est requis";
            isValid = false;
        }

        errors.fichier_photo = '';
        if (!formData.id && !formData.fichier_photo) {
            errors.fichier_photo ="La photo est requise";
            isValid = false;
        }

        isFormValid.value = isValid;
        
        return isValid;
    }

    function onFilePhotoUploaded(uploadedFile: { file: any, file_name: string, file_preview: string }) {
        formData.fichier_photo_preview = uploadedFile.file_preview;
        formData.fichier_photo = { file: uploadedFile.file, name: uploadedFile.file_name };
    }

    function onFileVideoUploaded(uploadedFile: { file: any, file_name: string, file_preview: string }) {
        formData.fichier_video_preview = uploadedFile.file_preview;
        formData.fichier_video = { file: uploadedFile.file, name: uploadedFile.file_name };
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {
            const formToUpload = new FormData();
            if (formData.id != null) {
                formToUpload.append("id", `${formData.id}`);
            }
            formToUpload.append("libelle", formData.libelle);
            formToUpload.append("contenu_aide", formData.contenu_aide);
            formToUpload.append("categorie_aide_id", formData.categorie_aide_id);
            if (formData.fichier_photo?.file) {
                formToUpload.append("fichier_photo", formData.fichier_photo.file, formData.fichier_photo.name);
            }
            if (formData.fichier_video?.file) {
                formToUpload.append("fichier_video", formData.fichier_video.file, formData.fichier_video.name);
            }

            await axios.post(`/backoffice/tutoriels/save`, formToUpload);
            router.push('/app/tutorials/list');
        } catch(error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-30"></div>
        <section class="s15_mobile px-4">
            <div class="widget px-7 py-7">
                <h4 class="font-semibold mb-5">Tutoriel / Edition <span class="text-sm text-grey-500"></span></h4>

                <div class="form-field-floating active my-4">
                    <select class="form-input-select" name="categorie_aide_id" id="categorie_aide_id" v-model="formData.categorie_aide_id">
                        <option value="" selected>Sélectionnez une catégorie</option>
                        <option :value="categorie.id" v-for="(categorie, index) in startupFormDatas.categories" :key="'categorie_' + index">
                            {{ categorie.libelle }}
                        </option>
                    </select>
                    <label for="categorie">Catégorie <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['categorie_aide_id']" class="text-red">{{ errors['categorie_aide_id'] }}</span>

                <div class="form-field-floating reactive active my-4">
                    <input type="text" class="form-input" v-model="formData.libelle" name="libelle" id="libelle" placeholder="">
                    <label for="libelle">Libellé <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['libelle']" class="text-red">{{ errors['libelle'] }}</span>

                <QuillEditor id="contenu_aide" v-model:content="formData.contenu_aide" contentType="html" theme="snow" style="height: 300px" />
                <span v-if="!!errors['contenu_aide']" class="text-red">{{ errors['contenu_aide'] }}</span>
                
                <div class="clear-both h-30"></div>
                <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                <span v-if="!!errors['fichier_photo']" class="text-red">{{ errors['fichier_photo'] }}</span>
                <p class="text-text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                <div class="flex gap-2">
                    <custom-button id="btn-submit" type="button" :classesList="'btn lg w-100'" :loading="loading" @click="onSubmit()">
                        <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                        <span>Enregistrer</span>
                    </custom-button>
                    <button class="btn stroke black lg">
                        <span>Effacer</span>
                    </button>
                </div>
                
            </div>
        </section>
        <section class="s5_mobile px-4">
            <div class="widget px-7 py-7">
                <h4 class="font-semibold mb-5 text-center">Photo <span class="text-sm text-grey-500"></span></h4>
                <div class="custom-upload-size margin-auto flex mb-8">
                    <custom-upload-input id="photo" label="Photo" type="IMAGE" :preview="formData.fichier_photo_preview" @uploaded="onFilePhotoUploaded"></custom-upload-input>
                </div>
                <h4 class="font-semibold mb-5 text-center">Vidéo <span class="text-sm text-grey-500"></span></h4>
                <div class="custom-upload-size margin-auto flex mb-8">
                    <custom-upload-input id="video" label="Vidéo" type="VIDEO" :preview="formData.fichier_video_preview" @uploaded="onFileVideoUploaded"></custom-upload-input>
                </div>
            </div>
        </section>
    </section>
</template>
